/**
 * ec-cart-lines.js
 *
 * Progressive enhancement for the full cart page qty steppers. The stepper
 * markup ships with a native <input type="number"> as the no-JS fallback
 * (form submit via the "Mettre à jour" button). When this module loads,
 * the +/− buttons fire debounced POSTs to /cart/change.js, the page
 * summary + line totals are patched from a soft HTML refresh (so Liquid
 * money_format stays authoritative), and CartUpdateEvent is dispatched so
 * <ec-header>'s badge and <ec-cart-drawer> stay in sync.
 *
 * Page-scoped: only binds inside .ec-cart-page.
 */

import { CartUpdateEvent } from '@theme/events';

const DEBOUNCE_MS = 280;
const SOURCE_ID = 'ec-cart-page';

class EcCartLines {
  #root;
  #debounceTimers = new Map();
  #inFlight = new Set();

  constructor(root) {
    this.#root = root;
    this.#root.addEventListener('click', this.#onClick);
    this.#root.addEventListener('change', this.#onInputChange);
  }

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const inc = target.closest('[data-ec-cart-qty-inc]');
    if (inc instanceof HTMLElement) {
      event.preventDefault();
      this.#bump(Number(inc.dataset.line), +1);
      return;
    }

    const dec = target.closest('[data-ec-cart-qty-dec]');
    if (dec instanceof HTMLElement) {
      event.preventDefault();
      this.#bump(Number(dec.dataset.line), -1);
    }
  };

  // Direct edits (typing, paste, mobile keyboard "Done") flush immediately
  // — the target qty is already known, no need to wait for more clicks.
  #onInputChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.classList.contains('ec-cart-qty__input')) return;
    const row = input.closest('.ec-cart-row');
    if (!(row instanceof HTMLElement)) return;
    const line = Number(row.dataset.line);
    if (!Number.isFinite(line)) return;
    const qty = Math.max(0, Math.floor(Number(input.value) || 0));
    input.value = String(qty);
    this.#scheduleFlush(line, qty, 0);
  };

  #bump(line, delta) {
    if (!Number.isFinite(line)) return;
    const row = this.#root.querySelector(`.ec-cart-row[data-line='${line}']`);
    if (!(row instanceof HTMLElement)) return;
    const input = row.querySelector('.ec-cart-qty__input');
    if (!(input instanceof HTMLInputElement)) return;
    const current = Math.max(0, Math.floor(Number(input.value) || 0));
    const next = Math.max(0, current + delta);
    if (next === current) return;
    input.value = String(next);
    this.#scheduleFlush(line, next, DEBOUNCE_MS);
  }

  #scheduleFlush(line, qty, delay) {
    const existing = this.#debounceTimers.get(line);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      this.#debounceTimers.delete(line);
      this.#flush(line, qty);
    }, delay);
    this.#debounceTimers.set(line, timer);
  }

  async #flush(line, qty) {
    if (this.#inFlight.has(line)) {
      // Re-queue: in-flight POST owns the round-trip; the latest target qty
      // becomes the next flush once it resolves.
      this.#scheduleFlush(line, qty, DEBOUNCE_MS);
      return;
    }
    const row = this.#root.querySelector(`.ec-cart-row[data-line='${line}']`);
    const input = row?.querySelector('.ec-cart-qty__input');
    if (!(row instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;

    this.#inFlight.add(line);
    row.classList.add('is-busy');

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity: qty }),
      });
      if (!res.ok) throw new Error(`cart/change failed: ${res.status}`);
      const cart = await res.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, SOURCE_ID, {
          source: SOURCE_ID,
          itemCount: cart.item_count,
        })
      );

      // Removal or empty cart can shift line numbering and re-render the
      // grid. A full reload is the simplest correct path; the dispatch
      // above keeps the header badge accurate during the navigation.
      if (qty === 0 || cart.item_count === 0) {
        await flushCartNotes();
        window.location.reload();
        return;
      }

      await this.#softRefresh();
    } catch (err) {
      console.error('[ec-cart-lines] cart/change failed', err);
      const prev = input.dataset.prevValue || '1';
      input.value = prev;
    } finally {
      row.classList.remove('is-busy');
      this.#inFlight.delete(line);
    }
  }

  // Re-fetch the cart page HTML and replace the summary, shipping bar, and
  // each row total. Keeps Liquid's money_format authoritative without
  // reimplementing it in JS. The full page reload is reserved for line
  // count changes (qty 0).
  async #softRefresh() {
    try {
      const res = await fetch(window.location.pathname, {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const incomingSummary = doc.querySelector('.ec-cart-summary');
      const currentSummary = document.querySelector('.ec-cart-summary');
      if (incomingSummary && currentSummary) currentSummary.replaceWith(incomingSummary);

      const incomingShip = doc.querySelector('.ec-shipping-progress');
      const currentShip = document.querySelector('.ec-shipping-progress');
      if (incomingShip && currentShip) currentShip.replaceWith(incomingShip);

      doc.querySelectorAll('.ec-cart-row').forEach((incomingRow) => {
        const line = incomingRow.dataset.line;
        if (!line) return;
        const currentRow = document.querySelector(`.ec-cart-row[data-line='${line}']`);
        if (!currentRow) return;
        const incomingTotal = incomingRow.querySelector('.ec-cart-row__total');
        const currentTotal = currentRow.querySelector('.ec-cart-row__total');
        if (incomingTotal && currentTotal) currentTotal.textContent = incomingTotal.textContent;
        const incomingPrice = incomingRow.querySelector('.ec-cart-row__price');
        const currentPrice = currentRow.querySelector('.ec-cart-row__price');
        if (incomingPrice && currentPrice) currentPrice.textContent = incomingPrice.textContent;
        const incomingInput = incomingRow.querySelector('.ec-cart-qty__input');
        const currentInput = currentRow.querySelector('.ec-cart-qty__input');
        if (incomingInput instanceof HTMLInputElement && currentInput instanceof HTMLInputElement) {
          currentInput.dataset.prevValue = incomingInput.value;
        }
      });
    } catch (err) {
      console.error('[ec-cart-lines] summary refresh failed', err);
    }
  }
}

// The reload below drops anything still sitting in <ec-cart-note>'s debounce,
// so every instance gets a chance to land its POST first. flush() always
// resolves, network error included — the reload must not be held hostage.
async function flushCartNotes() {
  const notes = Array.from(document.querySelectorAll('ec-cart-note'));
  await Promise.all(notes.map((note) => (typeof note.flush === 'function' ? note.flush() : null)));
}

function boot() {
  const root = document.querySelector('.ec-cart-page');
  if (root instanceof HTMLElement) new EcCartLines(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
